import React, { useState } from 'react';
import { AlertModal } from 'components';
import { Input, Modal, Select, TextArea } from 'common/components';
import styles from './AddPromptModal.module.scss';

type Tenant = {
    id: string;
    name: string;
};

type PromptData = {
    title: string;
    description: string;
    prompt: string;
    language: string;
    tenantId: string;
};

type AlertType = 'success' | 'error' | 'warning' | 'info';

type AddPromptModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (promptData: PromptData) => void;
    tenants?: Tenant[];
};

const AddPromptModal: React.FC<AddPromptModalProps> = ({ isOpen, onClose, onSave, tenants = [] }) => {
    const [promptName, setPromptName] = useState<string>('');
    const [tenant, setTenant] = useState<string>('');
    const [language, setLanguage] = useState<string>('english');
    const [description, setDescription] = useState<string>('');
    const [promptText, setPromptText] = useState<string>('');

    const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>('warning');

    const showAlert = (message: string, type: AlertType = 'warning'): void => {
        setAlertMessage(message);
        setAlertType(type);
        setIsAlertOpen(true);
    };

    const closeAlert = (): void => {
        setIsAlertOpen(false);
    };

    const handleSave = (): void => {
        if (!promptName.trim()) {
            showAlert('Please enter a prompt name', 'warning');
            return;
        }

        const promptData: PromptData = {
            title: promptName,
            description: description,
            prompt: promptText,
            language: language,
            tenantId: tenant,
        };

        onSave(promptData);
        resetForm();
    };

    const resetForm = (): void => {
        setPromptName('');
        setTenant('');
        setLanguage('english');
        setDescription('');
        setPromptText('');
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title='✨ Add New Prompt'
                size='md'
                onDone={handleSave}
                onCancel={onClose}
                doneText='Save Prompt'
                cancelText='Cancel'
            >
                <div className={styles.formContent}>
                    <Input
                        label='Prompt Name'
                        type='text'
                        value={promptName}
                        onChange={(e) => setPromptName(e.target.value)}
                        placeholder='Enter prompt name'
                    />

                    <Select
                        label='Tenant'
                        value={tenant}
                        onChange={(e) => setTenant(e.target.value)}
                        options={[
                            { value: '', label: 'Select tenant' },
                            ...tenants.map((tenant) => ({
                                value: tenant.id,
                                label: tenant.name,
                            })),
                        ]}
                    />

                    <div className={styles.languageField}>
                        <label className={styles.languageLabel}>Language</label>
                        <div className={styles.modalLanguageChips}>
                            <button
                                className={`${styles.modalLanguageChip} ${language === 'english' ? styles.active : ''}`}
                                onClick={() => setLanguage('english')}
                                type='button'
                            >
                                English
                            </button>
                            <button
                                className={`${styles.modalLanguageChip} ${language === 'spanish' ? styles.active : ''}`}
                                onClick={() => setLanguage('spanish')}
                                type='button'
                            >
                                Spanish
                            </button>
                        </div>
                    </div>

                    <TextArea
                        label='Description'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Enter prompt description'
                        rows={3}
                    />

                    <TextArea
                        label='Prompt'
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        placeholder='Enter your AI prompt here...'
                        rows={5}
                    />
                </div>
            </Modal>

            {/* @ts-ignore - AlertModal is not TypeScript yet */}
            <AlertModal isOpen={isAlertOpen} onClose={closeAlert} message={alertMessage} type={alertType} />
        </>
    );
};

export default AddPromptModal;
